import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { printDocument } from "@/lib/pdf-generator";
import { cn } from "@/lib/utils";

type DocumentType = "terms" | "privacy" | "disclaimer";

// Import the full text components from the popup
import {
  TermsOfServiceFullText,
  PrivacyPolicyFullText,
  DisclaimerFullText,
} from "@/components/full-legal-document-popup";

export default function LegalPage() {
  const [activeTab, setActiveTab] = useState<DocumentType>("terms");

  const handlePrint = () => {
    printDocument(activeTab);
  };

  const tabs: { value: DocumentType; label: string }[] = [
    { value: "terms", label: "Terms of Service" },
    { value: "privacy", label: "Privacy Policy" },
    { value: "disclaimer", label: "Disclaimer" },
  ];

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">Legal Documents</h1>
          <p className="text-muted-foreground">
            Review our terms of service, privacy policy, and disclaimer
          </p>
        </div>

        {/* Tabs Container */}
        <div className="bg-card border border-border rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            {/* Tabs List */}
            <div className="flex gap-2 border-b border-border">
              {tabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={cn(
                    "px-4 py-3 text-sm font-medium transition-all duration-200 border-b-2 -mb-[2px]",
                    activeTab === tab.value
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <Button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white flex items-center gap-2"
              title="Print document"
            >
              <Printer size={18} />
              Print
            </Button>
          </div>

          {/* Content */}
          <div className="mt-6 overflow-y-auto max-h-[600px] text-sm text-gray-700 dark:text-gray-300">
            <div data-document-type={activeTab}>
              {activeTab === "terms" && <TermsOfServiceFullText />}
              {activeTab === "privacy" && <PrivacyPolicyFullText />}
              {activeTab === "disclaimer" && <DisclaimerFullText />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
